import { styled } from '@mui/material/styles';
import MuiTableCell, {
  TableCellProps,
} from '@mui/material/TableCell';
import { ReactNode } from 'react';
import Switch from '@/components/Switch/Switch';
import Select from '../Select/Select';
import TextField from '@mui/material/TextField';
import { TableData, TableType } from './TableHelpers';

const TableCell = <T extends TableType>({
  rowId,
  children,
  dataField,
  dataRow,
  render,
  editable,
  onChange,
  head,
  required,
  error,
  formatError,
  dense = false,
  ...props
}: {
  rowId?: number;
  head?: boolean;
  editable?: boolean;
  required?: boolean;
  children?: ReactNode;
  dataField?: TableData;
  dataRow?: T;
  render?: (row: T, value?: TableData) => ReactNode;
  onChange?: (
    id: number,
    value: string | number | string[] | number[] | boolean,
  ) => void;
  error?: string;
  formatError?: (error: string, row: T) => string;
  dense?: boolean;
} & Omit<TableCellProps, 'onChange'>) => {
  let dataToDisplay:
    | string
    | number
    | string[]
    | boolean
    | ReactNode = dataField;
  let parsedData = dataField;

  if (render && dataRow) {
    dataToDisplay = render(dataRow, dataField);
  } else if (Array.isArray(dataField)) {
    if (editable) {
      dataToDisplay = (
        <Select
          placeholder="Select"
          aria-label={`Select value for row ${rowId}`}
          onChange={(e) =>
            rowId && e !== null && onChange?.(rowId, e)
          }
          options={dataField.map((d) => ({
            id: d,
            value: d,
            index: d,
            label: d,
          }))}
        />
      );
    } else {
      parsedData = dataField.join(', ');
      dataToDisplay = parsedData;
    }
  } else if (typeof dataField === 'boolean') {
    if (editable) {
      dataToDisplay = (
        <Switch
          checked={dataField}
          onChange={(e) => {
            if (!rowId) return;
            onChange?.(rowId, e.target.checked);
          }}
        />
      );
    } else {
      parsedData = dataField ? 'Yes' : 'No';
      dataToDisplay = parsedData;
    }
  } else if (typeof dataField === 'string' && editable) {
    const errorMessage = error
      ? formatError && dataRow
        ? formatError(error, dataRow)
        : error
      : undefined;
    const hasError = !!(required && !dataField) || !!error;

    dataToDisplay = (
      <TextField
        value={dataField}
        onChange={(e) => {
          if (!rowId) return;
          onChange?.(rowId, e.target.value);
        }}
        placeholder="Enter text"
        variant="outlined"
        size="small"
        fullWidth
        error={hasError}
        helperText={errorMessage}
      />
    );
  }

  return (
    <Root head={head} dense={dense} {...props}>
      {dataToDisplay || children}
    </Root>
  );
};

const Root = styled(MuiTableCell, {
  shouldForwardProp: (prop) => prop !== 'head' && prop !== 'dense',
})<{ head?: boolean; dense?: boolean } & Pick<TableCellProps, 'padding'>>(
  ({ head = false, dense = false, theme, padding }) => {
    const shouldReducePadding = dense && padding !== 'none' && padding !== 'checkbox';
    
    return {
      background: head ? theme.palette.grey100 : 'transparent',
      height: dense ? 40 : 60,
      border: 'none',
      ...(shouldReducePadding && {
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '8px',
        paddingRight: '8px',
      }),
    };
  },
);

export default TableCell;
